import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { Input } from "../components/ui/input";
import { 
  Bot, Phone, Calendar, Clock, Users, PlayCircle, PauseCircle,
  RefreshCw, AlertCircle, CheckCircle, TrendingUp, Zap,
  PhoneCall, PhoneOff, PhoneMissed, UserCheck, XCircle,
  Bell, ChevronRight, Activity, ExternalLink, Play, Pause,
  FileText, Mic, Volume2, ChevronDown, ChevronUp, Search,
  Filter, Download, Eye, Link, MapPin, Timer, MessageSquare
} from 'lucide-react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Disposition color mapping
const DISPOSITION_COLORS = {
  'Connected - Appointment Booked': 'bg-green-100 text-green-800 border-green-300',
  'Connected - Interested - Follow Up': 'bg-blue-100 text-blue-800 border-blue-300',
  'Connected - Call Later': 'bg-yellow-100 text-yellow-800 border-yellow-300',
  'Connected - Already Using Hearing Aid': 'bg-purple-100 text-purple-800 border-purple-300',
  'Connected - Not Interested': 'bg-red-100 text-red-800 border-red-300',
  'No Answer': 'bg-gray-100 text-gray-800 border-gray-300',
  'Busy': 'bg-orange-100 text-orange-800 border-orange-300',
  'Wrong Number': 'bg-pink-100 text-pink-800 border-pink-300',
  'Do Not Call': 'bg-red-200 text-red-900 border-red-400',
  'Voicemail': 'bg-indigo-100 text-indigo-800 border-indigo-300',
};

// Status icons
const STATUS_ICONS = {
  'pending': <Clock className="w-4 h-4 text-gray-500" />,
  'processing': <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />,
  'call_triggered': <PhoneCall className="w-4 h-4 text-green-500 animate-pulse" />,
  'completed': <CheckCircle className="w-4 h-4 text-green-600" />,
  'error': <XCircle className="w-4 h-4 text-red-500" />,
  'no_answer': <PhoneMissed className="w-4 h-4 text-yellow-500" />,
};

export default function AIAutomationDashboard({ user, logout }) {
  const [systemStatus, setSystemStatus] = useState(null);
  const [queueData, setQueueData] = useState(null);
  const [todayStats, setTodayStats] = useState(null);
  const [callHistory, setCallHistory] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [whatsappLogs, setWhatsappLogs] = useState([]);
  const [whatsappStats, setWhatsappStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [expandedCall, setExpandedCall] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [playingAudio, setPlayingAudio] = useState(null);

  // Fetch WhatsApp logs
  const fetchWhatsappLogs = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/huskyvoice/whatsapp/logs?limit=100`);
      setWhatsappLogs(res.data.logs || []);
      setWhatsappStats(res.data.stats || {});
    } catch (error) {
      console.error('Error fetching WhatsApp logs:', error);
    }
  }, []);

  // Fetch all data
  const fetchData = useCallback(async () => {
    try {
      const [statusRes, queueRes, statsRes, historyRes, notifRes] = await Promise.all([
        axios.get(`${API_URL}/api/ai-automation/status`),
        axios.get(`${API_URL}/api/ai-automation/queue/today`),
        axios.get(`${API_URL}/api/ai-automation/stats/today`),
        axios.get(`${API_URL}/api/ai-automation/calls/history?limit=100`),
        axios.get(`${API_URL}/api/ai-automation/notifications?limit=20`)
      ]);

      setSystemStatus(statusRes.data);
      setQueueData(queueRes.data);
      setTodayStats(statsRes.data);
      setCallHistory(historyRes.data.calls || []);
      setNotifications(notifRes.data.notifications || []);
    } catch (error) {
      console.error('Error fetching AI automation data:', error);
      // Still try to fetch individual endpoints
      try {
        const statusRes = await axios.get(`${API_URL}/api/ai-automation/status`);
        setSystemStatus(statusRes.data);
      } catch (e) {}
      try {
        const queueRes = await axios.get(`${API_URL}/api/ai-automation/queue/today`);
        setQueueData(queueRes.data);
      } catch (e) {}
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchWhatsappLogs();
    // Auto-refresh every 10 seconds for real-time updates
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [fetchData, fetchWhatsappLogs]);

  // Generate daily queue
  const generateQueue = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API_URL}/api/ai-automation/queue/generate`);
      toast.success(`Queue generated: ${response.data.queue_generated?.total_leads || 0} leads`);
      fetchData();
    } catch (error) {
      toast.error('Failed to generate queue');
    } finally {
      setGenerating(false);
    }
  };

  // Process queue
  const processQueue = async (maxCalls = null) => {
    setProcessing(true);
    try {
      const response = await axios.post(`${API_URL}/api/ai-automation/process`, { max_calls: maxCalls });
      if (response.data.status === 'outside_operating_hours') {
        toast.warning(response.data.message);
      } else if (response.data.status === 'processed') {
        toast.success(`Processed ${response.data.total_processed} leads`);
      } else {
        toast.info(response.data.message || 'Processing complete');
      }
      fetchData();
    } catch (error) {
      toast.error('Failed to process queue');
    } finally {
      setProcessing(false);
    }
  };

  // Format duration
  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Format time ago
  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString();
  };

  // Filter calls
  const filteredCalls = callHistory.filter(call => {
    const matchesSearch = searchQuery === '' || 
      call.lead_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      call.phone?.includes(searchQuery);
    const matchesFilter = filterStatus === 'all' || call.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return (
      <DashboardLayout user={user} logout={logout}>
        <div className="flex items-center justify-center h-96">
          <RefreshCw className="w-8 h-8 animate-spin text-teal-600" />
        </div>
      </DashboardLayout>
    );
  }

  const isOperating = systemStatus?.operating_hours?.is_active;
  const progressPercent = systemStatus?.today?.queue_size 
    ? (systemStatus.today.calls_made / systemStatus.today.queue_size) * 100 
    : 0;

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="p-6 space-y-6" data-testid="ai-automation-dashboard">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-3">
              <Bot className="w-8 h-8 text-teal-600" />
              AI Automation Dashboard
            </h1>
            <p className="text-slate-600 mt-1">
              Priya AI - Real-time Call Monitoring & Management
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Badge 
              variant={isOperating ? "default" : "secondary"}
              className={isOperating ? "bg-green-500 animate-pulse" : "bg-slate-500"}
            >
              {isOperating ? "LIVE" : "Offline"}
            </Badge>
            <span className="text-sm text-slate-500">
              {systemStatus?.operating_hours?.start} - {systemStatus?.operating_hours?.end} IST
            </span>
            <Button variant="outline" size="sm" onClick={fetchData}>
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-6 gap-4">
          <Card className="bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500">Total Leads</p>
                  <p className="text-2xl font-bold text-slate-800">
                    {systemStatus?.leads?.total?.toLocaleString() || 0}
                  </p>
                </div>
                <Users className="w-8 h-8 text-slate-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-blue-600">In Queue</p>
                  <p className="text-2xl font-bold text-blue-800">
                    {systemStatus?.today?.pending || 0}
                  </p>
                </div>
                <Clock className="w-8 h-8 text-blue-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-amber-600">In Progress</p>
                  <p className="text-2xl font-bold text-amber-800">
                    {callHistory.filter(c => c.status === 'call_triggered' || c.status === 'processing').length}
                  </p>
                </div>
                <PhoneCall className="w-8 h-8 text-amber-400 animate-pulse" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-green-600">Calls Made</p>
                  <p className="text-2xl font-bold text-green-800">
                    {systemStatus?.today?.calls_made || 0}
                  </p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <p className="text-xs text-green-500 mt-1">
                {systemStatus?.today?.in_progress || 0} triggered today
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-purple-600">Appointments</p>
                  <p className="text-2xl font-bold text-purple-800">
                    {todayStats?.appointments_booked || 0}
                  </p>
                </div>
                <Calendar className="w-8 h-8 text-purple-400" />
              </div>
              <p className="text-xs text-purple-500 mt-1">
                {todayStats?.conversion_rate?.toFixed(1) || 0}% conversion
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-teal-50 to-teal-100 border-teal-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-teal-600">Remaining</p>
                  <p className="text-2xl font-bold text-teal-800">
                    {systemStatus?.today?.remaining_capacity || 0}
                  </p>
                </div>
                <Zap className="w-8 h-8 text-teal-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Progress Bar */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-4">
                <span className="text-sm font-medium text-slate-700">Daily Progress</span>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span className="text-xs text-slate-500">Completed</span>
                  <div className="w-3 h-3 rounded-full bg-amber-500 animate-pulse"></div>
                  <span className="text-xs text-slate-500">In Progress</span>
                  <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                  <span className="text-xs text-slate-500">Pending</span>
                </div>
              </div>
              <span className="text-sm text-slate-500">
                {systemStatus?.today?.calls_made || 0} / {systemStatus?.daily_limit || 250} daily limit
              </span>
            </div>
            <Progress value={progressPercent} className="h-4" />
          </CardContent>
        </Card>

        {/* Control Panel */}
        <Card className="border-2 border-teal-200">
          <CardHeader className="bg-gradient-to-r from-teal-50 to-cyan-50 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-teal-600" />
                <CardTitle className="text-lg">Control Panel</CardTitle>
              </div>
              {!isOperating && (
                <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300">
                  Outside operating hours
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Button 
                onClick={generateQueue} 
                disabled={generating}
                variant="outline"
                className="border-blue-300 text-blue-700 hover:bg-blue-50"
              >
                {generating ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Users className="w-4 h-4 mr-2" />}
                Generate Queue
              </Button>

              <div className="h-8 w-px bg-slate-200" />

              <Button 
                onClick={() => processQueue(10)}
                disabled={processing || !isOperating}
                className="bg-green-600 hover:bg-green-700"
              >
                {processing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-2" />}
                Process 10
              </Button>

              <Button 
                onClick={() => processQueue(50)}
                disabled={processing || !isOperating}
                className="bg-teal-600 hover:bg-teal-700"
              >
                <Zap className="w-4 h-4 mr-2" />
                Process 50
              </Button>

              <Button 
                onClick={() => processQueue()}
                disabled={processing || !isOperating}
              >
                <PhoneCall className="w-4 h-4 mr-2" />
                Process All
              </Button>

              <div className="flex-1" />

              <Button variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50">
                <PauseCircle className="w-4 h-4 mr-2" />
                Pause
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Main Content - Call Activity */}
        <Tabs defaultValue="activity" className="space-y-4">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="activity" className="flex items-center gap-2">
              <Activity className="w-4 h-4" /> Live Activity
            </TabsTrigger>
            <TabsTrigger value="queue" className="flex items-center gap-2">
              <Clock className="w-4 h-4" /> Queue ({systemStatus?.today?.pending || 0})
            </TabsTrigger>
            <TabsTrigger value="completed" className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" /> Calls Made ({systemStatus?.today?.calls_made || 0})
            </TabsTrigger>
            <TabsTrigger value="whatsapp" className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4" /> WhatsApp ({whatsappStats?.sent_today || 0})
            </TabsTrigger>
            <TabsTrigger value="appointments" className="flex items-center gap-2">
              <Calendar className="w-4 h-4" /> Appointments ({todayStats?.appointments_booked || 0})
            </TabsTrigger>
          </TabsList>

          {/* Live Activity Tab */}
          <TabsContent value="activity">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                    Live Call Activity
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <Input 
                        placeholder="Search by name or phone..." 
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-9 w-64"
                      />
                    </div>
                    <select 
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      className="border rounded px-3 py-2 text-sm"
                    >
                      <option value="all">All Status</option>
                      <option value="call_triggered">In Progress</option>
                      <option value="completed">Completed</option>
                      <option value="pending">Pending</option>
                      <option value="error">Failed</option>
                    </select>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[500px]">
                  <div className="divide-y">
                    {filteredCalls.length === 0 ? (
                      <div className="p-8 text-center text-slate-500">
                        <Phone className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                        <p>No calls yet. Generate a queue and start processing.</p>
                      </div>
                    ) : (
                      filteredCalls.map((call, idx) => (
                        <CallActivityRow 
                          key={call.call_id || idx}
                          call={call}
                          isExpanded={expandedCall === call.call_id}
                          onToggle={() => setExpandedCall(expandedCall === call.call_id ? null : call.call_id)}
                          formatDuration={formatDuration}
                          formatTimeAgo={formatTimeAgo}
                        />
                      ))
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Queue Tab */}
          <TabsContent value="queue">
            <Card>
              <CardHeader>
                <CardTitle>Pending Queue - {queueData?.date}</CardTitle>
                <CardDescription>Leads waiting to be called</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-white">
                      <tr className="border-b bg-slate-50">
                        <th className="text-left p-3">#</th>
                        <th className="text-left p-3">Lead</th>
                        <th className="text-left p-3">Phone</th>
                        <th className="text-left p-3">City</th>
                        <th className="text-left p-3">Source</th>
                        <th className="text-left p-3">Previous Disposition</th>
                        <th className="text-left p-3">Attempts</th>
                        <th className="text-left p-3">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {queueData?.pending_preview?.map((item, idx) => (
                        <tr key={item.lead_id} className="border-b hover:bg-slate-50">
                          <td className="p-3 text-slate-500">{item.priority}</td>
                          <td className="p-3">
                            <a 
                              href={`/leads?search=${item.lead_id}`}
                              className="font-medium text-teal-600 hover:underline flex items-center gap-1"
                            >
                              {item.name || 'Unknown'}
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </td>
                          <td className="p-3 font-mono">{item.phone}</td>
                          <td className="p-3">{item.city || '-'}</td>
                          <td className="p-3">
                            <Badge variant="outline" className="text-xs">{item.source}</Badge>
                          </td>
                          <td className="p-3">
                            {item.previous_disposition ? (
                              <Badge variant="secondary" className="text-xs">
                                {item.previous_disposition}
                              </Badge>
                            ) : (
                              <span className="text-slate-400">New</span>
                            )}
                          </td>
                          <td className="p-3">{item.total_attempts || 0}</td>
                          <td className="p-3">
                            <Button size="sm" variant="ghost" className="text-teal-600">
                              <Phone className="w-4 h-4 mr-1" /> Call Now
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Completed Tab */}
          <TabsContent value="completed">
            <Card>
              <CardHeader>
                <CardTitle>Completed Calls Today</CardTitle>
                <CardDescription>All processed calls with outcomes</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  <div className="space-y-2">
                    {callHistory.filter(c => ['completed', 'call_triggered', 'failed', 'no_answer'].includes(c.status)).map((call, idx) => (
                      <CompletedCallCard 
                        key={call.call_id || idx}
                        call={call}
                        formatDuration={formatDuration}
                      />
                    ))}
                    {callHistory.filter(c => ['completed', 'call_triggered', 'failed', 'no_answer'].includes(c.status)).length === 0 && (
                      <div className="p-8 text-center text-slate-500">
                        No processed calls yet
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* WhatsApp Tab */}
          <TabsContent value="whatsapp">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <MessageSquare className="w-5 h-5 text-green-600" />
                      WhatsApp Messages Sent
                    </CardTitle>
                    <CardDescription>
                      Contextual messages sent by Priya during calls
                    </CardDescription>
                  </div>
                  <div className="flex gap-4 text-sm">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">{whatsappStats?.sent_today || 0}</div>
                      <div className="text-slate-500">Today</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">{whatsappStats?.total_sent || 0}</div>
                      <div className="text-slate-500">Total</div>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Message Type Stats */}
                <div className="flex gap-2 mb-4 flex-wrap">
                  {Object.entries(whatsappStats?.by_type || {}).map(([type, count]) => (
                    <Badge key={type} variant="outline" className="px-3 py-1">
                      {type}: {count}
                    </Badge>
                  ))}
                </div>
                
                <ScrollArea className="h-[400px]">
                  <div className="space-y-2">
                    {whatsappLogs.map((log, idx) => (
                      <div key={idx} className="p-3 border rounded-lg bg-white hover:bg-slate-50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-full ${
                              log.message_type === 'clinic' ? 'bg-blue-100' :
                              log.message_type === 'pricing' ? 'bg-green-100' :
                              log.message_type === 'appointment' ? 'bg-purple-100' :
                              'bg-yellow-100'
                            }`}>
                              <MessageSquare className={`w-4 h-4 ${
                                log.message_type === 'clinic' ? 'text-blue-600' :
                                log.message_type === 'pricing' ? 'text-green-600' :
                                log.message_type === 'appointment' ? 'text-purple-600' :
                                'text-yellow-600'
                              }`} />
                            </div>
                            <div>
                              <div className="font-medium">{log.phone}</div>
                              <div className="text-sm text-slate-500">
                                {log.message_type?.toUpperCase()} 
                                {log.params?.city && ` - ${log.params.city}`}
                                {log.params?.category && ` - ${log.params.category}`}
                                {log.params?.info_type && ` - ${log.params.info_type}`}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <Badge className={log.result ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                              {log.result ? 'Sent' : 'Failed'}
                            </Badge>
                            <div className="text-xs text-slate-500 mt-1">
                              {log.timestamp ? new Date(log.timestamp).toLocaleString('en-IN', {
                                hour: '2-digit',
                                minute: '2-digit',
                                day: '2-digit',
                                month: 'short'
                              }) : '-'}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    {whatsappLogs.length === 0 && (
                      <div className="p-8 text-center text-slate-500">
                        No WhatsApp messages sent yet
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Appointments Tab */}
          <TabsContent value="appointments">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-green-600" />
                  Appointments Booked Today
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  <div className="space-y-3">
                    {notifications.filter(n => n.type === 'appointment_booked').map((appt, idx) => (
                      <div key={idx} className="p-4 border rounded-lg bg-green-50 border-green-200">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <div className="p-2 rounded-full bg-green-100">
                              <Calendar className="w-5 h-5 text-green-600" />
                            </div>
                            <div>
                              <p className="font-semibold text-green-800">{appt.lead_name}</p>
                              <p className="text-sm text-green-600">{appt.lead_phone}</p>
                              <div className="flex items-center gap-4 mt-2 text-sm">
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-4 h-4" />
                                  {appt.appointment_date}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Clock className="w-4 h-4" />
                                  {appt.appointment_time}
                                </span>
                                <span className="flex items-center gap-1">
                                  <MapPin className="w-4 h-4" />
                                  {appt.appointment_clinic || 'TBD'}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <Badge className="bg-green-600">Booked by AI</Badge>
                            <p className="text-xs text-slate-500 mt-1">
                              {new Date(appt.created_at).toLocaleTimeString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                    {notifications.filter(n => n.type === 'appointment_booked').length === 0 && (
                      <div className="p-8 text-center text-slate-500">
                        <Calendar className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                        <p>No appointments booked today yet</p>
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}

// Call Activity Row Component
function CallActivityRow({ call, isExpanded, onToggle, formatDuration, formatTimeAgo }) {
  const statusClass = {
    'pending': 'bg-gray-100 border-gray-300',
    'processing': 'bg-blue-50 border-blue-300',
    'call_triggered': 'bg-amber-50 border-amber-300 animate-pulse',
    'completed': 'bg-green-50 border-green-300',
    'error': 'bg-red-50 border-red-300',
  }[call.status] || 'bg-gray-50';

  return (
    <div className={`border-l-4 ${statusClass}`}>
      {/* Main Row */}
      <div 
        className="p-4 cursor-pointer hover:bg-slate-50 flex items-center gap-4"
        onClick={onToggle}
      >
        {/* Status Icon */}
        <div className="flex-shrink-0">
          {STATUS_ICONS[call.status] || <Phone className="w-4 h-4" />}
        </div>

        {/* Lead Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <a 
              href={`/leads?search=${call.lead_id}`}
              className="font-medium text-slate-800 hover:text-teal-600 hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {call.lead_name || 'Unknown'}
            </a>
            <ExternalLink className="w-3 h-3 text-slate-400" />
          </div>
          <p className="text-sm text-slate-500 font-mono">{call.phone}</p>
        </div>

        {/* City */}
        <div className="w-24 text-sm text-slate-600">
          {call.city || '-'}
        </div>

        {/* Duration */}
        <div className="w-20 text-sm">
          {call.duration ? (
            <span className="flex items-center gap-1">
              <Timer className="w-3 h-3" />
              {formatDuration(call.duration)}
            </span>
          ) : '-'}
        </div>

        {/* Disposition */}
        <div className="w-48">
          {call.disposition ? (
            <Badge 
              variant="outline" 
              className={`text-xs ${DISPOSITION_COLORS[call.disposition] || 'bg-gray-100'}`}
            >
              {call.disposition.replace('Connected - ', '')}
            </Badge>
          ) : (
            <Badge variant="outline" className="text-xs">
              {call.status === 'call_triggered' ? 'Calling...' : call.status}
            </Badge>
          )}
        </div>

        {/* Appointment Badge */}
        <div className="w-28">
          {call.appointment_booked ? (
            <Badge className="bg-green-600 text-xs">
              <Calendar className="w-3 h-3 mr-1" />
              Booked
            </Badge>
          ) : call.status === 'completed' ? (
            <span className="text-xs text-slate-400">No Appt</span>
          ) : null}
        </div>

        {/* Time */}
        <div className="w-20 text-xs text-slate-500">
          {formatTimeAgo(call.created_at)}
        </div>

        {/* Expand Icon */}
        <div className="flex-shrink-0">
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-slate-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-slate-400" />
          )}
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="px-4 pb-4 bg-slate-50 border-t">
          <div className="grid grid-cols-3 gap-4 pt-4">
            {/* Recording */}
            <div>
              <p className="text-xs font-medium text-slate-500 mb-2">Recording</p>
              {call.recording_url ? (
                <audio controls className="w-full h-8">
                  <source src={call.recording_url} type="audio/mpeg" />
                </audio>
              ) : (
                <p className="text-sm text-slate-400">No recording available</p>
              )}
            </div>

            {/* Appointment Details */}
            <div>
              <p className="text-xs font-medium text-slate-500 mb-2">Appointment</p>
              {call.appointment_booked ? (
                <div className="text-sm space-y-1">
                  <p><span className="text-slate-500">Date:</span> {call.appointment_date || 'TBD'}</p>
                  <p><span className="text-slate-500">Time:</span> {call.appointment_time || 'TBD'}</p>
                  <p><span className="text-slate-500">Clinic:</span> {call.appointment_clinic || 'TBD'}</p>
                </div>
              ) : (
                <p className="text-sm text-slate-400">Not booked</p>
              )}
            </div>

            {/* Call Summary */}
            <div>
              <p className="text-xs font-medium text-slate-500 mb-2">AI Summary</p>
              <p className="text-sm text-slate-600">
                {call.summary || 'No summary available'}
              </p>
            </div>
          </div>

          {/* Transcript */}
          {call.transcript && (
            <div className="mt-4">
              <p className="text-xs font-medium text-slate-500 mb-2">Transcript</p>
              <div className="bg-white border rounded p-3 max-h-40 overflow-y-auto">
                <p className="text-sm text-slate-600 whitespace-pre-wrap">
                  {call.transcript}
                </p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="mt-4 flex items-center gap-2">
            <a 
              href={`/leads?search=${call.lead_id}`}
              className="text-sm text-teal-600 hover:underline flex items-center gap-1"
            >
              <ExternalLink className="w-4 h-4" />
              View Lead Profile
            </a>
            {call.recording_url && (
              <a 
                href={call.recording_url}
                download
                className="text-sm text-slate-600 hover:underline flex items-center gap-1 ml-4"
              >
                <Download className="w-4 h-4" />
                Download Recording
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Completed Call Card Component
function CompletedCallCard({ call, formatDuration }) {
  const getStatusColor = (status) => {
    switch(status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'call_triggered': return 'bg-blue-100 text-blue-800';
      case 'failed': return 'bg-red-100 text-red-800';
      case 'no_answer': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className={`p-4 border rounded-lg ${call.appointment_booked ? 'bg-green-50 border-green-200' : 'bg-white hover:bg-slate-50'}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {call.appointment_booked ? (
            <div className="p-2 rounded-full bg-green-100">
              <Calendar className="w-5 h-5 text-green-600" />
            </div>
          ) : call.status === 'call_triggered' ? (
            <div className="p-2 rounded-full bg-blue-100">
              <Phone className="w-5 h-5 text-blue-600" />
            </div>
          ) : (
            <div className="p-2 rounded-full bg-slate-100">
              <PhoneCall className="w-5 h-5 text-slate-600" />
            </div>
          )}
          <div>
            <a 
              href={`/leads?search=${call.lead_id || call.phone}`}
              className="font-medium text-slate-800 hover:text-teal-600 hover:underline"
            >
              {call.lead_name || call.name || 'Unknown'}
            </a>
            <p className="text-sm text-slate-500">
              {call.phone} • {call.city || call.source || 'Unknown'}
            </p>
          </div>
        </div>
        <div className="text-right">
          <div className="flex gap-2 justify-end">
            <Badge variant="outline" className={`text-xs ${getStatusColor(call.status)}`}>
              {call.status === 'call_triggered' ? 'Called' : call.status || 'Unknown'}
            </Badge>
            {call.disposition && (
              <Badge 
                variant="outline" 
                className={`text-xs ${DISPOSITION_COLORS[call.disposition] || 'bg-gray-100'}`}
              >
                {call.disposition?.replace('Connected - ', '')}
              </Badge>
            )}
          </div>
          <p className="text-xs text-slate-500 mt-1">
            {call.duration ? `Duration: ${formatDuration(call.duration)}` : call.triggered_at ? new Date(call.triggered_at).toLocaleTimeString('en-IN', {hour: '2-digit', minute: '2-digit'}) : ''}
          </p>
        </div>
      </div>
      {call.summary && (
        <p className="text-sm text-slate-600 mt-2 pl-12">
          {call.summary}
        </p>
      )}
    </div>
  );
}
